<template>
  <div :style="{ height: frameHeight + 'px' }">
    <header ref="header" :class="$style.header">
      <div :class="[$style.grid, 'container']">
        <div>
          <nav v-if="obj.back">
            <BackLink
              :component="parent"
              group="finding_aids"
              label="Finding Aids"
              path="/library-archives/archives-collections"
            ></BackLink>
          </nav>
          <section v-if="cart && cart.length">
            <router-link
              :class="['button-secondary', 'space-12-16-above']"
              :to="{ path: '/finding-aids/requested-aids' }"
              :ga4-event="
                JSON.stringify({
                  click_type: 'request',
                  component: `${parent}`,
                  content_group: 'finding_aids',
                  gtm_tag: 'link',
                  link_text: 'Request selected items',
                  link_url: '/finding-aids/requested-aids',
                })
              "
            >
              Request {{ cart.length }} item(s)
            </router-link>
          </section>
        </div>
        <SearchForm
          :class="$style.form"
          :pre-hint="'Search the Archives'"
          :show-hint="false"
          :show-label="false"
          :type="'archives'"
        ></SearchForm>
      </div>
    </header>
  </div>
</template>

<script setup lang="ts">
import BackLink from '@/components/common/BackLink.vue';
import { onMounted, ref, useCssModule, watch } from 'vue';
import SearchForm from '@/components/search/SearchForm.vue';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/stores/mainStore.js';
import { useRouteParent } from '@/composables/Common.js';

defineProps<{
  obj: {
    back: string;
  };
}>();

const store = useMainStore();
const { cart, showBanner } = storeToRefs(store);
const { parent } = useRouteParent();
const style = useCssModule();

const frameHeight = ref();
const header = ref();
const heights = ref({
  'main-nav': 0,
});
const navTop = ref();

onMounted(() => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      checkHeights();
    });
  });

  window.addEventListener('scroll', () => {
    checkPos();
  });
});

watch(store.cart, () => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      frameHeight.value = header.value.offsetHeight;
    });
  });
});

watch(showBanner, () => {
  checkHeights();
});

function checkHeights() {
  const banner = document.getElementById('guggen-banner');
  const main = document.getElementById('main-nav');

  if (banner) {
    heights.value['banner'] = banner.getBoundingClientRect().height;
  }

  frameHeight.value = header.value.offsetHeight;
  navTop.value =
    window.scrollY !== 0
      ? window.scrollY + header.value.getBoundingClientRect().top
      : header.value.getBoundingClientRect().top;

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const type = entry.target.id;

      heights.value[type] = entry.contentBoxSize[0].blockSize;
    }
  });

  resizeObserver.observe(main);
}

function checkPos() {
  const curPos = window.scrollY + heights.value['main-nav'];

  if (
    header.value &&
    !header.value.classList.contains(`${style.fixed}`) &&
    curPos >= navTop.value
  ) {
    header.value.classList.add(`${style.fixed}`);
  }

  if (header.value && header.value.classList.contains(`${style.fixed}`) && curPos <= navTop.value) {
    header.value.classList.remove(`${style.fixed}`);
  }
}
</script>

<style lang="scss">
@use '@/styles/forms.module.scss' as *;
</style>

<style lang="scss" module>
.form {
  margin-top: 0.5rem;
}

// Unused?
.frame {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
}

.fixed {
  padding: 0 1rem 2rem;
  position: fixed;
}

.grid {
  section {
    a {
      margin-top: 1.5rem;
    }
  }
}

.header {
  background: $white;
  left: 0;
  padding-bottom: 2rem;
  right: 0;
  top: px-to-rem(57);
  z-index: get-zindex(content);

  &::after {
    @include line;

    & {
      margin-top: 2rem;
    }
  }
}

@media (min-width: $break-point) {
  .form {
    grid-column: span 4;
    justify-content: flex-end;
    margin-top: 0;

    input[type=search] {
      flex: 0;
    }
  }

  .grid {
    @include grid;

    > div {
      grid-column: span 8;
    }

    form {
      align-self: start;
      grid-column: 9 / span 4;
      grid-row: 1;
    }
  }

  .header {
    top: px-to-rem(170);

    .form {
      margin-top: 0;
    }
  }

  :global(.fixed) {
    .header {
      top: px-to-rem(150);
    }
  }
}
</style>
